import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, BookOpen, FileCheck, UserPlus, Mail, X, Search, BarChart3, Clock } from 'lucide-react';
import { toThumbnailUrl } from '@/utils/drive';

const ALL = '__all__';

export default function AdminCourses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [assignCourse, setAssignCourse] = useState(null); // course object o null

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.getCourses();
      setCourses(res.data);
    } catch (e) { toast.error('Error al cargar cursos'); }
  };

  const handleDelete = async (id, t) => {
    if (!window.confirm(`¿Eliminar curso "${t}"?`)) return;
    try {
      await api.deleteCourse(id);
      toast.success('Curso eliminado');
      load();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl" data-testid="admin-courses-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight flex items-center gap-3">
              <BookOpen className="h-7 w-7 text-[#DC143C]" />
              Cursos
            </h1>
            <p className="text-gray-600 mt-1">Gestión de cursos y asignaciones de la Academia E-Learning</p>
          </div>
          <Button data-testid="new-course-button" onClick={() => navigate('/admin/courses/new')} className="bg-[#DC143C] hover:bg-[#B91C1C] text-white font-medium uppercase tracking-wide flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Nuevo Curso
          </Button>
        </div>

        {courses.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">
            <BookOpen className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No hay cursos. Crea el primero haciendo clic en "Nuevo Curso".</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((c) => (
              <div key={c.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow" data-testid="course-card">
                {c.thumbnail_url ? (
                  <img src={toThumbnailUrl(c.thumbnail_url, 480)} alt={c.title} className="w-full h-40 object-cover bg-gray-100" onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <div className="w-full h-40 bg-gradient-to-br from-[#DC143C] to-[#B91C1C] flex items-center justify-center">
                    <BookOpen className="h-16 w-16 text-white/40" />
                  </div>
                )}
                <div className="p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">{c.title}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{c.description}</p>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-4">
                    <span>{(c.slides || []).length} slides</span>
                    <span>•</span>
                    <span>{(c.questions || []).length} preguntas</span>
                    <span>•</span>
                    <span>Aprobación: {c.passing_score}%</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="default" size="sm" data-testid="assign-course-button" onClick={() => setAssignCourse(c)} className="bg-[#DC143C] hover:bg-[#B91C1C] text-white flex items-center justify-center gap-1">
                      <UserPlus className="h-4 w-4" /> Asignar
                    </Button>
                    <Button variant="outline" size="sm" data-testid="edit-course-button" onClick={() => navigate(`/admin/courses/${c.id}`)} className="flex items-center justify-center gap-1">
                      <Pencil className="h-4 w-4" /> Editar
                    </Button>
                    <Button variant="outline" size="sm" data-testid="diploma-course-button" onClick={() => navigate(`/admin/courses/${c.id}/diploma`)} className="flex items-center justify-center gap-1">
                      <FileCheck className="h-4 w-4" /> Diploma
                    </Button>
                    <Button variant="outline" size="sm" data-testid="attempts-course-button" onClick={() => navigate(`/admin/courses/${c.id}/intentos`)} className="flex items-center justify-center gap-1 border-blue-200 text-blue-700 hover:bg-blue-50">
                      <BarChart3 className="h-4 w-4" /> Intentos
                    </Button>
                    <Button variant="ghost" size="sm" data-testid="delete-course-button" onClick={() => handleDelete(c.id, c.title)} className="col-span-2 text-red-600 hover:bg-red-50 flex items-center justify-center gap-1">
                      <Trash2 className="h-4 w-4" /> Eliminar
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {assignCourse && (
          <AssignCourseDialog course={assignCourse} onClose={() => setAssignCourse(null)} />
        )}
      </div>
    </DashboardLayout>
  );
}

// ============== Modal de asignación =====================
function AssignCourseDialog({ course, onClose }) {
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allPersonal, setAllPersonal] = useState([]);
  const [assigned, setAssigned] = useState([]); // array of { user_id, expires_at, is_expired, days_left }
  const [filterDept, setFilterDept] = useState(ALL);
  const [filterProj, setFilterProj] = useState(ALL);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [extendingId, setExtendingId] = useState(null); // user_id being extended

  const reloadAssignments = async () => {
    try {
      const a = await api.getCourseAssignments(course.id);
      setAssigned(a.data || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [d, p, allP, a] = await Promise.all([
          api.getDepartments(),
          api.getProjects(),
          api.getPersonal(),
          api.getCourseAssignments(course.id),
        ]);
        setDepartments(d.data);
        setProjects(p.data);
        setAllPersonal(allP.data);
        setAssigned(a.data || []);
      } catch (e) { toast.error('Error al cargar datos'); }
      finally { setLoading(false); }
    })();
  }, [course.id]);

  const assignedMap = new Map(assigned.map((a) => [a.user_id, a]));
  const assignedIds = assigned.map((a) => a.user_id);

  const visible = allPersonal.filter((u) => {
    if (filterDept !== ALL && u.department_id !== filterDept) return false;
    if (filterProj !== ALL && u.project_id !== filterProj) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!(u.full_name || '').toLowerCase().includes(s) && !(u.username || '').toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const toggle = (uid) => {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelected(next);
  };

  const toggleAll = () => {
    const all = visible.filter((u) => !assignedIds.includes(u.id)).map((u) => u.id);
    if (all.every((id) => selected.has(id))) {
      const next = new Set(selected);
      all.forEach((id) => next.delete(id));
      setSelected(next);
    } else {
      setSelected(new Set([...selected, ...all]));
    }
  };

  const handleSave = async () => {
    if (selected.size === 0) { toast.error('Selecciona al menos un usuario'); return; }
    setSaving(true);
    try {
      const r = await api.assignCourse(course.id, Array.from(selected));
      toast.success(r.data.message + (r.data.notified?.length ? ` · ${r.data.notified.length} email(s) enviados` : ''));
      await reloadAssignments();
      setSelected(new Set());
    } catch (e) { toast.error('Error al asignar'); }
    finally { setSaving(false); }
  };

  const handleUnassign = async (userId) => {
    if (!window.confirm('¿Quitar la asignación de este usuario?')) return;
    try {
      await api.unassignCourse(course.id, userId);
      setAssigned(assigned.filter((a) => a.user_id !== userId));
      toast.success('Asignación eliminada');
    } catch (e) { toast.error('Error al desasignar'); }
  };

  const handleExtend = async (userId, days) => {
    setExtendingId(userId);
    try {
      const r = await api.extendCourseAssignment(course.id, userId, days);
      toast.success(`Periodo extendido +${days} día(s)`);
      // Update local state with new expires_at
      setAssigned(assigned.map((a) => a.user_id === userId
        ? { ...a, expires_at: r.data.expires_at, is_expired: false, days_left: days }
        : a
      ));
    } catch (e) { toast.error('Error al extender'); }
    finally { setExtendingId(null); }
  };

  const fmtDate = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }); } catch { return iso; }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold uppercase tracking-tight">Asignar Curso</DialogTitle>
          <p className="text-sm text-gray-600 mt-1">{course.title}</p>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-gray-500">Cargando...</div>
        ) : (
          <div className="space-y-4 mt-3">
            {/* Filtros */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Select value={filterDept} onValueChange={setFilterDept}>
                <SelectTrigger data-testid="assign-filter-dept"><SelectValue placeholder="Departamento" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos los departamentos</SelectItem>
                  {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterProj} onValueChange={setFilterProj}>
                <SelectTrigger data-testid="assign-filter-project"><SelectValue placeholder="Proyecto" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos los proyectos</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input className="pl-8" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>

            {/* Lista personal */}
            <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
              <div className="flex items-center gap-3 p-3 bg-gray-50 border-b border-gray-200 sticky top-0">
                <input
                  type="checkbox"
                  data-testid="assign-toggle-all"
                  checked={visible.filter(u => !assignedIds.includes(u.id)).every(u => selected.has(u.id)) && visible.some(u => !assignedIds.includes(u.id))}
                  onChange={toggleAll}
                  className="h-4 w-4"
                />
                <span className="text-xs font-medium uppercase text-gray-600">Seleccionar todos ({visible.filter(u => !assignedIds.includes(u.id)).length} disponibles)</span>
              </div>
              {visible.length === 0 ? (
                <div className="p-6 text-center text-gray-500 text-sm">No se encontró personal</div>
              ) : visible.map((u) => {
                const assignment = assignedMap.get(u.id);
                const isAssigned = !!assignment;
                const isExpired = !!assignment?.is_expired;
                const daysLeft = assignment?.days_left;
                return (
                  <div key={u.id} data-testid="assign-personal-row" className={`flex items-center gap-3 p-3 border-b border-gray-100 ${isExpired ? 'bg-red-50' : isAssigned ? 'bg-green-50' : ''}`}>
                    <input
                      type="checkbox"
                      disabled={isAssigned}
                      checked={selected.has(u.id) || isAssigned}
                      onChange={() => toggle(u.id)}
                      className="h-4 w-4"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{u.full_name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {u.username} · {u.department_name} · {u.project_name}
                        {u.email ? <span className="text-blue-600 inline-flex items-center gap-1 ml-2"><Mail className="h-3 w-3" />{u.email}</span> : <span className="text-amber-600 ml-2">(sin email)</span>}
                      </p>
                      {isAssigned && assignment.expires_at && (
                        <p className={`text-xs mt-1 flex items-center gap-1 ${isExpired ? 'text-red-700 font-medium' : daysLeft <= 1 ? 'text-red-600' : daysLeft <= 3 ? 'text-amber-700' : 'text-gray-600'}`}>
                          <Clock className="h-3 w-3" />
                          {isExpired
                            ? <>Expiró el {fmtDate(assignment.expires_at)}</>
                            : <>Vence el {fmtDate(assignment.expires_at)} · {daysLeft === 0 ? 'hoy' : daysLeft === 1 ? '1 día restante' : `${daysLeft} días restantes`}</>
                          }
                        </p>
                      )}
                    </div>
                    {isAssigned && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {[
                          { d: 1, label: '+1d' },
                          { d: 3, label: '+3d' },
                          { d: 7, label: '+7d' },
                          { d: 365, label: '+1a' },
                        ].map((opt) => (
                          <Button
                            key={opt.d}
                            variant="outline"
                            size="sm"
                            disabled={extendingId === u.id}
                            onClick={() => handleExtend(u.id, opt.d)}
                            data-testid="extend-assignment-button"
                            title={opt.d === 365 ? 'Extender +1 año' : `Extender +${opt.d} día${opt.d > 1 ? 's' : ''}`}
                            className="text-xs px-2 h-7 border-amber-200 text-amber-700 hover:bg-amber-50"
                          >
                            {opt.label}
                          </Button>
                        ))}
                        <Button variant="ghost" size="sm" onClick={() => handleUnassign(u.id)} className="text-red-600 hover:bg-red-50" data-testid="unassign-button">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="text-xs text-gray-500">
              <span className="inline-block w-3 h-3 bg-green-100 border border-green-300 rounded mr-1"></span> Ya asignado · Se enviará email a quienes tengan correo configurado.
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || selected.size === 0} data-testid="confirm-assign-button" className="bg-[#DC143C] hover:bg-[#B91C1C] text-white">
            {saving ? 'Asignando...' : `Asignar (${selected.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
